import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { partyShellIdToModePathSegment } from "../../modes/shellToModeApi";
import { PartySavedRosterSetupSection } from "../../party/PartySavedRosterSetupSection";
import { PartyPlayResumePrompt } from "../../party/PartyPlayResumePrompt";
import { usePartySavedRoster } from "../../party/usePartySavedRoster";
import { PartyTimer, PartyTopBar } from "../../party/PartyChrome";
import { PartyMascot } from "../../party/PartyMascot";
import { PartyTrialSetupBanner } from "../../party/PartyTrialSetupBanner";
import { TRIAL_DARE_DECK } from "../../party/partyTrialMockData";
import { usePartyPlayTrialMode } from "../../party/usePartyPlayTrialMode";
import PartyShareResultButton from "../../party/share/PartyShareResultButton";
import { usePartyShareModeTitle } from "../../party/share/usePartyShareModeTitle";
import { invalidateUserSessionCredits } from "../../hooks/useAuth";
import { handlePartyRosterNameEnter } from "../../party/partyNameInputEnter";
import type { ModeDareCardRow } from "../../types/api";
import { apiClient } from "../../utils/api";
import { trackPartyGameCompleted, trackPartyGameStarted } from "../../utils/mixpanel";
import { clearPartyGameCache, readPartyGameCache, writePartyGameCache } from "../../utils/partyGameLocalCache";

const SEG = partyShellIdToModePathSegment("challenge");
const MAX_ENTRANTS = 2;
const CHALLENGE_SECONDS = 60;

type Mode = "solo" | "teams";

const CACHE_SHELL = "challenge";

type DareGameCacheData = {
  mode: Mode;
  names: string[];
  deck: ModeDareCardRow[];
  idx: number;
  scores: Record<number, number>;
  currentTurn: number;
};

// --- styled -----------------------------------------------------------------

const SetupRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const PlayRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const EndRoot = styled(Box)({
  padding: "16px 20px 100px",
  textAlign: "center",
  minHeight: "100dvh",
  boxSizing: "border-box",
});

const MascotWrap = styled(Box)({
  marginTop: 8,
  marginBottom: 12,
});

const HintText = styled(Typography)({
  display: "block",
  color: "var(--ink-dim)",
  fontSize: 14,
  marginBottom: 12,
  lineHeight: 1.5,
});

const ModeRow = styled(Stack)({
  flexDirection: "row",
  gap: 8,
  marginBottom: 12,
});

const NameRow = styled(Stack)({
  flexDirection: "row",
  gap: 8,
  marginBottom: 12,
});

const TeamField = styled(TextField)({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "var(--ink)",
    "& fieldset": {
      borderColor: "var(--ink-faint)",
      borderWidth: 1.5,
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--hot)",
    },
  },
});

const PlayerChipsRow = styled(Stack)({
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 6,
  marginBottom: 12,
});

const PlayerChip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "6px 6px 6px 12px",
  borderRadius: 999,
  background: "var(--ink-faint)",
  fontWeight: 700,
  color: "var(--ink)",
});

const ChipRemove = styled(IconButton)({
  width: 28,
  height: 28,
  padding: 0,
  background: "rgba(255,255,255,0.1)",
  color: "var(--ink)",
  "&:hover": { background: "rgba(255,255,255,0.18)" },
});

const AddPlayerIconButton = styled(IconButton)({
  width: 48,
  height: 48,
  flexShrink: 0,
  borderRadius: 16,
  border: "1.5px solid transparent",
  boxSizing: "border-box",
  "&.Mui-disabled": {
    cursor: "not-allowed",
    opacity: 1,
    border: "1.5px dashed color-mix(in oklab, var(--ink-dim) 55%, transparent)",
    backgroundColor: "rgba(255,255,255,0.02)",
    color: "var(--ink-dim)",
    WebkitTextFillColor: "var(--ink-dim)",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)",
    "& .MuiSvgIcon-root": {
      opacity: 0.38,
    },
  },
});

const PreviewNote = styled(Typography)({
  fontSize: 12,
  color: "var(--ink-dim)",
  marginBottom: 8,
});

const StartButton = styled(Button)({
  width: "100%",
});

const CreditErrorText = styled(Typography)({
  display: "block",
  color: "var(--hot)",
  marginTop: 8,
});

const TurnBanner = styled(Box)({
  marginBottom: 12,
  padding: "14px 16px 16px",
  borderRadius: 20,
  textAlign: "center",
  background: "linear-gradient(165deg, color-mix(in oklab, var(--gold) 20%, transparent), rgba(255,255,255,0.04))",
  border: "1.5px solid color-mix(in oklab, var(--gold) 50%, transparent)",
  boxShadow: "0 0 0 2px rgba(255, 200, 120, 0.35), 0 16px 40px -12px rgba(255, 200, 100, 0.28)",
});

const TurnKicker = styled(Box)({
  fontSize: 11,
  letterSpacing: "0.14em",
  color: "var(--ink-dim)",
  marginBottom: 6,
});

const TurnName = styled(Box)({
  fontSize: "clamp(22px, 6vw, 32px)",
  lineHeight: 1.1,
  color: "var(--gold)",
});

const PlayHeadRow = styled(Stack)({
  flexDirection: "row",
  alignItems: "stretch",
  justifyContent: "center",
  gap: 16,
  marginBottom: 12,
});

const TimerWrap = styled(Stack)({
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const DareInstruction = styled(Typography)({
  fontSize: 14,
  color: "var(--ink-dim)",
  marginBottom: 12,
  lineHeight: 1.45,
  textAlign: "center",
});

const DareCard = styled(Box)({
  "--card-bg": "var(--paper)",
  color: "var(--paper-ink)",
  padding: 22,
  marginBottom: 16,
  textAlign: "center",
});

const ScoreRow = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  gap: 10,
  marginBottom: 14,
});

const ScorePill = styled(Box, {
  shouldForwardProp: (p) => p !== "active",
})<{ active?: boolean }>(({ active }) => ({
  flex: 1,
  minWidth: 0,
  padding: "10px 12px",
  borderRadius: 16,
  textAlign: "center",
  border: active ? "2px solid color-mix(in oklab, var(--gold) 65%, transparent)" : "1.5px solid var(--ink-faint)",
  background: active ? "color-mix(in oklab, var(--gold) 12%, transparent)" : "rgba(255,255,255,0.03)",
}));

const ScorePillLabel = styled(Typography)({
  fontSize: 10,
  letterSpacing: "0.08em",
  color: "var(--ink-dim)",
  fontWeight: 800,
});

const ScorePillName = styled(Typography)({
  fontSize: 13,
  fontWeight: 800,
  marginTop: 4,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const ScorePillValue = styled(Typography)({
  fontSize: 26,
  fontWeight: 900,
  marginTop: 4,
  fontFamily: "var(--party-display-font, inherit)",
});

const ActionStack = styled(Stack)({
  flexDirection: "column",
  gap: 10,
});

const TimerHint = styled(Typography)({
  fontSize: 12,
  color: "var(--ink-dim)",
  marginTop: 12,
  textAlign: "center",
});

const ResultsKicker = styled(Box)({
  fontSize: 12,
  letterSpacing: "0.2em",
  color: "var(--hot)",
  fontWeight: 800,
  marginBottom: 8,
});

const ResultsTitle = styled(Box)({
  fontSize: "clamp(26px, 8vw, 36px)",
  lineHeight: 1.1,
  marginBottom: 8,
});

const RunnerSection = styled(Box)({
  marginTop: 24,
  textAlign: "start",
  maxWidth: 420,
  marginLeft: "auto",
  marginRight: "auto",
});

const RunnerHeading = styled(Typography)({
  fontSize: 11,
  letterSpacing: "0.14em",
  color: "var(--ink-dim)",
  fontWeight: 800,
  marginBottom: 10,
});

const RunnerRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 0",
  borderBottom: "1px solid var(--ink-faint)",
});

const HomeButton = styled(Button)({
  width: "100%",
  marginTop: 24,
});

// --- component --------------------------------------------------------------

export default function PartyPlayDareGame() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrialMode = usePartyPlayTrialMode();
  const savedRoster = usePartySavedRoster();
  const shareModeTitle = usePartyShareModeTitle("challenge", i18n.language?.startsWith("ar") ? "تحديات" : "Challenges");
  const [mode, setMode] = useState<Mode>("solo");
  const [names, setNames] = useState<string[]>([]);
  const [newName, setNewName] = useState("");
  const [deck, setDeck] = useState<ModeDareCardRow[]>([]);
  const [idx, setIdx] = useState(0);
  const [scores, setScores] = useState<Record<number, number>>({});
  const [currentTurn, setCurrentTurn] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(CHALLENGE_SECONDS);
  const [gameStartedAtMs, setGameStartedAtMs] = useState<number | null>(null);
  const completionTrackedRef = useRef(false);

  const namesRef = useRef(names);
  useEffect(() => {
    namesRef.current = names;
  }, [names]);

  const [cacheEnvelope] = useState(() => readPartyGameCache<DareGameCacheData>(CACHE_SHELL));
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const cachedHasProgress =
    cacheEnvelope != null &&
    Array.isArray(cacheEnvelope.data.deck) &&
    cacheEnvelope.data.deck.length > 0 &&
    typeof cacheEnvelope.data.idx === "number" &&
    cacheEnvelope.data.idx < cacheEnvelope.data.deck.length;
  const showResumePrompt = cachedHasProgress && !resumeDismissed;

  const resolvedForIdxRef = useRef<number | null>(null);
  const deckRef = useRef(deck);
  const idxRef = useRef(idx);
  const currentTurnRef = useRef(currentTurn);
  deckRef.current = deck;
  idxRef.current = idx;
  currentTurnRef.current = currentTurn;

  const previewQ = useQuery({
    queryKey: ["modes", SEG, "cards"],
    queryFn: () => apiClient.getDareCards(SEG),
    enabled: deck.length === 0 && !isTrialMode,
  });

  const startMut = useMutation({
    mutationFn: () => apiClient.postDareStart(SEG),
    onSuccess: (data: { dares?: ModeDareCardRow[]; truths?: ModeDareCardRow[]; lies?: ModeDareCardRow[] }) => {
      savedRoster.recordPlayersBatch(namesRef.current);
      const dares = data?.dares ?? [];
      const truths = data?.truths ?? [];
      const lies = data?.lies ?? [];
      const list: ModeDareCardRow[] = dares.length ? dares : truths.length ? truths : lies.length ? lies : [];
      const dareDeckKind =
        dares.length && truths.length
          ? "mixed"
          : dares.length
            ? "dares"
            : truths.length
              ? "truths"
              : lies.length
                ? "lies"
                : undefined;
      setDeck(Array.isArray(list) ? list : []);
      setIdx(0);
      setScores({});
      setCurrentTurn(0);
      setGameStartedAtMs(Date.now());
      completionTrackedRef.current = false;
      resolvedForIdxRef.current = null;
      invalidateUserSessionCredits(queryClient);
      trackPartyGameStarted({
        shell_id: "challenge",
        category_names: dareDeckKind ? [`deck:${dareDeckKind}`] : [],
        number_of_players: names.length,
        trial: false,
        mode_name_en: "Dare / challenge",
        dare_mode: mode,
        dare_deck_kind: dareDeckKind,
        deck_size: list.length,
      });
    },
  });

  const beginDareTrial = useCallback(() => {
    savedRoster.recordPlayersBatch(namesRef.current);
    const list = TRIAL_DARE_DECK.map((c) => ({ ...c }));
    setDeck(list);
    setIdx(0);
    setScores({});
    setCurrentTurn(0);
    setGameStartedAtMs(Date.now());
    completionTrackedRef.current = false;
    resolvedForIdxRef.current = null;
    trackPartyGameStarted({
      shell_id: "challenge",
      category_names: ["trial_sample"],
      number_of_players: names.length,
      trial: true,
      mode_name_en: "Dare / challenge",
      dare_mode: mode,
      dare_deck_kind: "dares",
      deck_size: list.length,
    });
  }, [mode, names.length, savedRoster]);

  const addName = () => {
    const n = newName.trim();
    if (!n || names.length >= MAX_ENTRANTS) return;
    setNames((p) => [...p, n]);
    setNewName("");
    savedRoster.recordPlayerAdded(n);
  };

  const removeNameAt = (i: number) => setNames((p) => p.filter((_, j) => j !== i));

  const onExitPlayToSetup = useCallback(() => {
    clearPartyGameCache(CACHE_SHELL);
    setDeck([]);
  }, []);

  useEffect(() => {
    const onResults = deck.length > 0 && idx >= deck.length;
    if (onResults) {
      clearPartyGameCache(CACHE_SHELL);
      return;
    }
    const id = window.setTimeout(() => {
      const payload: DareGameCacheData = {
        mode,
        names,
        deck,
        idx,
        scores,
        currentTurn,
      };
      writePartyGameCache(CACHE_SHELL, payload);
    }, 400);
    return () => window.clearTimeout(id);
  }, [deck, idx, mode, names, scores, currentTurn]);

  useEffect(() => {
    const onResults = deck.length > 0 && idx >= deck.length;
    if (!onResults || gameStartedAtMs == null || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackPartyGameCompleted({
      shell_id: "challenge",
      trial: isTrialMode,
      mode_name_en: "Dare / challenge",
      duration_seconds: Math.max(0, Math.round((Date.now() - gameStartedAtMs) / 1000)),
    });
  }, [deck.length, gameStartedAtMs, idx, isTrialMode]);

  const applyDareCache = useCallback((data: DareGameCacheData) => {
    setMode(data.mode === "teams" ? "teams" : "solo");
    setNames(Array.isArray(data.names) ? data.names : []);
    setDeck(Array.isArray(data.deck) ? data.deck : []);
    setIdx(typeof data.idx === "number" ? data.idx : 0);
    setScores(data.scores && typeof data.scores === "object" ? data.scores : {});
    setCurrentTurn(typeof data.currentTurn === "number" ? data.currentTurn % MAX_ENTRANTS : 0);
    resolvedForIdxRef.current = null;
  }, []);

  const resolveChallenge = useCallback((ok: boolean, snap?: { frozenIdx: number; frozenTurn: number }) => {
    const frozenIdx = snap?.frozenIdx ?? idxRef.current;
    const frozenTurn = snap?.frozenTurn ?? currentTurnRef.current;
    if (idxRef.current !== frozenIdx) return;
    if (resolvedForIdxRef.current === frozenIdx) return;
    resolvedForIdxRef.current = frozenIdx;

    const card = deckRef.current[frozenIdx];
    if (ok && card) {
      const pts = card.points ?? 1;
      setScores((s) => ({
        ...s,
        [frozenTurn]: (s[frozenTurn] ?? 0) + pts,
      }));
    }

    setIdx((prev) => {
      if (prev !== frozenIdx) return prev;
      const next = frozenIdx + 1;
      const len = deckRef.current.length;
      return next >= len ? len : next;
    });
    setCurrentTurn(() => (frozenTurn + 1) % MAX_ENTRANTS);
  }, []);

  const resolveRef = useRef(resolveChallenge);
  resolveRef.current = resolveChallenge;

  useEffect(() => {
    if (deck.length === 0 || idx >= deck.length) return;

    const cardIndexWhenShown = idx;
    const lockedTurn = currentTurn;

    let left = CHALLENGE_SECONDS;
    setSecondsLeft(left);
    const id = window.setInterval(() => {
      left -= 1;
      setSecondsLeft(left);
      if (left <= 0) {
        window.clearInterval(id);
        if (idxRef.current === cardIndexWhenShown) {
          resolveRef.current(false, { frozenIdx: cardIndexWhenShown, frozenTurn: lockedTurn });
        }
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [deck.length, idx, currentTurn]);

  const card = deck[idx];
  const cardText = useMemo(() => {
    const ar = card?.text_ar?.trim();
    const en = card?.text_en?.trim();
    if (i18n.language?.startsWith("ar")) return ar || en || "";
    return en || ar || "";
  }, [card?.text_ar, card?.text_en, i18n.language]);

  const activeName = names[currentTurn] ?? "";

  if (deck.length === 0) {
    const canStart = names.length === MAX_ENTRANTS;
    const addDisabled = names.length >= MAX_ENTRANTS || !newName.trim();
    const addDisabledTitle = names.length >= MAX_ENTRANTS
      ? t("party.playDareGame.addDisabledTitleFull")
      : !newName.trim()
        ? t("party.playDareGame.addDisabledTitleEmpty")
        : undefined;
    const addAriaLabel = addDisabled
      ? `${t("party.playDareGame.addPlayerAria")} — ${addDisabledTitle}`
      : t("party.playDareGame.addPlayerAria");
    return (
      <SetupRoot>
        <PartyTopBar title={t("party.playDareGame.setupTitleBar")} onBack={() => navigate("/party/detail/challenge")} />
        <MascotWrap sx={{ textAlign: "center" }}>
          <PartyMascot shellId="challenge" size={100} />
        </MascotWrap>
        <ModeRow>
          <Button
            type="button"
            fullWidth
            className={mode === "solo" ? "party-btn party-btn-hot" : "party-btn party-btn-paper"}
            onClick={() => setMode("solo")}
          >
            {t("party.playDareGame.modeSolo")}
          </Button>
          <Button
            type="button"
            fullWidth
            className={mode === "teams" ? "party-btn party-btn-hot" : "party-btn party-btn-paper"}
            onClick={() => setMode("teams")}
          >
            {t("party.playDareGame.modeTeams")}
          </Button>
        </ModeRow>
        {isTrialMode && <PartyTrialSetupBanner />}
        <HintText>{mode === "teams" ? t("party.playDareGame.setupHintTeams") : t("party.playDareGame.setupHintSolo")}</HintText>
        <NameRow>
          <TeamField
            size="small"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={mode === "teams" ? t("party.playDareGame.namePlaceholderTeam") : t("party.playDareGame.namePlaceholderPlayer")}
            disabled={names.length >= MAX_ENTRANTS}
            onKeyDown={(e) => handlePartyRosterNameEnter(e, addName)}
            hiddenLabel
            InputProps={{ "aria-label": t("party.playDareGame.nameFieldAria") }}
          />
          <AddPlayerIconButton
            type="button"
            onClick={addName}
            disabled={addDisabled}
            title={addDisabled ? addDisabledTitle : undefined}
            aria-label={addAriaLabel}
            className="party-btn party-btn-paper"
          >
            <AddIcon />
          </AddPlayerIconButton>
        </NameRow>
        <PlayerChipsRow>
          {names.map((n, i) => (
            <PlayerChip key={`${n}-${i}`}>
              {n}
              <ChipRemove type="button" size="small" aria-label={t("party.playDareGame.removePlayerAria")} onClick={() => removeNameAt(i)}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </ChipRemove>
            </PlayerChip>
          ))}
        </PlayerChipsRow>
        <PartySavedRosterSetupSection
          api={savedRoster}
          variant="players"
          currentPlayers={names}
          maxPlayers={MAX_ENTRANTS}
          onQuickAddPlayer={(name) => {
            setNames((p) => (p.length >= MAX_ENTRANTS ? p : [...p, name]));
            savedRoster.recordPlayerAdded(name);
          }}
        />
        {!canStart && <HintText sx={{ mb: 1 }}>{t("party.playDareGame.needTwoHint")}</HintText>}
        {!isTrialMode && previewQ.data && (
          <PreviewNote>
            {t("party.playDareGame.previewAdmin", { count: previewQ.data.length })}
          </PreviewNote>
        )}
        {showResumePrompt && cacheEnvelope ? (
          <PartyPlayResumePrompt
            savedAtIso={cacheEnvelope.savedAt}
            onContinue={() => {
              applyDareCache(cacheEnvelope.data);
              setResumeDismissed(true);
            }}
            onNewGame={() => {
              clearPartyGameCache(CACHE_SHELL);
              setResumeDismissed(true);
            }}
          />
        ) : (
          <StartButton
            type="button"
            disabled={!canStart || (!isTrialMode && startMut.isPending)}
            className="party-btn party-btn-hot"
            sx={{ opacity: canStart ? 1 : 0.5 }}
            onClick={() => (isTrialMode ? beginDareTrial() : startMut.mutate())}
          >
            {isTrialMode ? t("party.trial.continueSampleCta") : t("party.playDareGame.startCta")}
          </StartButton>
        )}
        {!isTrialMode && (startMut.error as { response?: { status?: number } })?.response?.status === 402 && (
          <CreditErrorText>{t("party.playDareGame.creditInsufficient")}</CreditErrorText>
        )}
      </SetupRoot>
    );
  }

  if (idx >= deck.length) {
    const ranked = names
      .map((n, i) => ({ n, i, sc: scores[i] ?? 0 }))
      .sort((a, b) => b.sc - a.sc);
    const top = ranked[0]?.sc ?? 0;
    const tieAtTop = ranked.filter((r) => r.sc === top).length > 1;
    const champions = ranked.filter((r) => r.sc === top);

    return (
      <EndRoot>
        <PartyTopBar title={t("party.playDareGame.endTitleBar")} onBack={() => navigate("/party/detail/challenge")} />
        <MascotWrap sx={{ textAlign: "center" }}>
          <PartyMascot shellId="challenge" size={tieAtTop ? 96 : 120} />
        </MascotWrap>
        <ResultsKicker className="party-mono party-anim-fade-scale-in">{t("party.playDareGame.resultsKicker")}</ResultsKicker>
        <ResultsTitle className="party-display party-anim-fade-scale-in">{t("party.playDareGame.resultsTitle")}</ResultsTitle>
        <Typography className="party-mono" sx={{ fontSize: 14, color: "var(--ink-dim)", fontWeight: 700 }}>
          {t("party.playDareGame.resultsSub")}
        </Typography>

        {champions.length > 0 && (
          <Box sx={{ mt: 3 }} className="party-anim-fade-scale-in">
            <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.14em", color: "var(--ink-dim)", mb: 1 }}>
              {tieAtTop ? t("party.playDareGame.championTie") : t("party.playDareGame.championSolo")}
            </Typography>
            <Typography className="party-display" sx={{ fontSize: "clamp(28px, 8vw, 40px)", color: "var(--gold)", lineHeight: 1.15 }}>
              {tieAtTop ? champions.map((c) => c.n).join(" · ") : champions[0]?.n}
            </Typography>
            <Typography sx={{ mt: 1, fontWeight: 800, color: "var(--ink)" }}>
              {champions[0]?.sc ?? 0}{" "}
              {(champions[0]?.sc ?? 0) === 1 ? t("party.playDareGame.point") : t("party.playDareGame.points")}
            </Typography>
          </Box>
        )}

        {ranked.length > 1 && (
          <RunnerSection>
            <RunnerHeading className="party-mono">{t("party.playDareGame.fullStandings")}</RunnerHeading>
            {ranked.map((r) => (
              <RunnerRow key={r.i}>
                <Typography fontWeight={800}>{r.n}</Typography>
                <Typography fontWeight={700} color="var(--ink-dim)">
                  {r.sc}{" "}
                  {r.sc === 1 ? t("party.playDareGame.point") : t("party.playDareGame.points")}
                </Typography>
              </RunnerRow>
            ))}
          </RunnerSection>
        )}

        <PartyShareResultButton
          className="party-btn party-btn-paper"
          sx={{ width: "100%", maxWidth: 420, mt: 2 }}
          model={{
            shellId: "challenge",
            modeTitle: shareModeTitle,
            resultTitle: t("party.playDareGame.resultsTitle"),
            hero: tieAtTop ? champions.map((c) => c.n).join(" · ") : champions[0]?.n ?? "",
            sub: `${champions[0]?.sc ?? 0} ${((champions[0]?.sc ?? 0) === 1 ? t("party.playDareGame.point") : t("party.playDareGame.points"))}`,
            standings: ranked.slice(0, 4).map((r, idx) => ({
              name: r.n,
              value: `${r.sc} ${r.sc === 1 ? t("party.playDareGame.point") : t("party.playDareGame.points")}`,
              highlight: idx === 0,
            })),
          }}
        />

        <HomeButton type="button" className="party-btn party-btn-hot" onClick={() => navigate("/")}>
          {t("party.playDareGame.home")}
        </HomeButton>
      </EndRoot>
    );
  }

  return (
    <PlayRoot>
      <PartyTopBar
        title={t("party.playDareGame.playTopBar", { current: idx + 1, total: deck.length })}
        onBack={onExitPlayToSetup}
      />
      <PlayHeadRow>
        <TurnBanner className="party-anim-fade-scale-in" sx={{ flex: 1, minWidth: 0 }}>
          <TurnKicker className="party-mono">
            {mode === "teams" ? t("party.playDareGame.turnKickerTeam") : t("party.playDareGame.turnKickerSolo")}
          </TurnKicker>
          <TurnName className="party-display">{activeName}</TurnName>
        </TurnBanner>
        <TimerWrap>
          <PartyTimer seconds={secondsLeft} total={CHALLENGE_SECONDS} />
        </TimerWrap>
      </PlayHeadRow>

      <DareInstruction>
        {mode === "teams"
          ? t("party.playDareGame.doDareInstructionTeam", { name: activeName })
          : t("party.playDareGame.doDareInstructionSolo", { name: activeName })}
      </DareInstruction>

      <ScoreRow>
        {names.map((n, i) => (
          <ScorePill key={`${n}-${i}`} active={i === currentTurn}>
            <ScorePillLabel className="party-mono">
              {mode === "teams" ? t("party.playDareGame.scoreLabelTeam") : t("party.playDareGame.scoreLabelPlayer")}
            </ScorePillLabel>
            <ScorePillName>{n}</ScorePillName>
            <ScorePillValue className="party-display">{scores[i] ?? 0}</ScorePillValue>
          </ScorePill>
        ))}
      </ScoreRow>

      <DareCard className="party-kan-card party-anim-fade-scale-in">
        <Typography className="party-mono" sx={{ fontSize: 11, mb: 1, opacity: 0.85 }} component="div">
          {t("party.playDareGame.pointsOnCard", { points: card?.points ?? 1 })}
        </Typography>
        <Typography className="party-display" sx={{ fontSize: "clamp(22px, 5vw, 30px)", lineHeight: 1.35 }} component="div">
          {cardText}
        </Typography>
      </DareCard>

      <ActionStack>
        <Button type="button" className="party-btn party-btn-hot" fullWidth onClick={() => resolveChallenge(true)}>
          {t("party.playDareGame.completedCta", { name: activeName })}
        </Button>
        <Button type="button" className="party-btn party-btn-paper" fullWidth onClick={() => resolveChallenge(false)}>
          {t("party.playDareGame.failedCta", { name: activeName })}
        </Button>
      </ActionStack>

      <TimerHint>{t("party.playDareGame.timerHint")}</TimerHint>
    </PlayRoot>
  );
}
